import isEmpty from 'lodash/isEmpty'
import isNil from 'lodash/isNil'
import { useCallback } from 'react'

import { InvoiceDiscountType } from '@features/invoices/enums/invoiceDiscountType'
import { InvoiceLineType } from '@features/invoices/enums/invoiceLineType'
import type { InvoiceFormRowLine } from '@features/invoices/types/invoiceForm/invoiceFormLine/invoiceFormRowLine'
import { InvoiceFormReceiver } from '@features/invoices/types/invoiceForm/invoiceFormReceiver'
import { calculateRowLineTotals } from '@features/invoices/utils/calculateRowLineTotals'
import { clampInvoiceDiscountValue } from '@features/invoices/utils/clampInvoiceDiscountValue'
import { getAmountExcludingVat } from '@features/invoices/utils/getAmountExcludingVat'
import { InvoiceDiscountCalculator } from '@features/invoices/utils/invoiceDiscountCalculator'
import { isInvoiceFormRowLine } from '@features/invoices/utils/isInvoiceFormRowLine'
import { sumRowLineTotals } from '@features/invoices/utils/sumRowLineTotals'
import { ProductType } from '@features/products/enums/productType'
import { ProductUnit } from '@features/products/enums/productUnit'
import { Product } from '@features/products/types/product'
import { isInvoiceProductType } from '@features/products/utils/isInvoiceProductType'
import { useFilteredVatRates } from '@features/vatRates/hooks/useFilteredVatRates'
import { useGetVatRate } from '@features/vatRates/hooks/useGetVatRate'
import { VatRate } from '@features/vatRates/types/vatRate'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { NumberValue } from '../../../../../../../../../utils'
import { useInvoiceEditorForm } from '../../../../../contexts/invoiceEditorFormContext'
import { useInvoiceEditorDocumentType } from '../../../../../hooks/useInvoiceEditorDocumentType'
import { getUnitPrice } from '../utils/getUnitPrice'

export const useUpdateDependentLineValues = () => {
  const { organization } = useUserOrganization()
  const { getVatRate } = useGetVatRate()
  const { getFilteredVatRates } = useFilteredVatRates()
  const { invoiceEditorForm } = useInvoiceEditorForm()
  const { isCreditNote } = useInvoiceEditorDocumentType()
  const { getValues, setValue } = invoiceEditorForm
  const shouldPreventVatUpdates = organization?.isVatExempted

  const setVatRate = useCallback(
    (lineIndex: number, vatRate?: VatRate) => {
      setValue(`invoiceLines.${lineIndex}.vatRateId`, vatRate?.id)
      setValue(`invoiceLines.${lineIndex}.vatRatePercent`, vatRate?.rate)
    },
    [setValue],
  )

  const getVatRateList = useCallback(
    (productType?: ProductType, receiver?: InvoiceFormReceiver) => {
      const contact = receiver || getValues('receiver')

      return getFilteredVatRates({
        contactCountryCode: contact?.countryId,
        contactType: contact?.type,
        productType,
      })
    },
    [getFilteredVatRates, getValues],
  )

  const recalculateSubtotals = useCallback(
    (hasLineStructureChanged?: boolean) => {
      const lines = getValues('invoiceLines')
      const hasSubtotalLines = lines.some((line) => line.type === InvoiceLineType.Subtotal)

      if (!lines || lines.length < 1 || !hasSubtotalLines) {
        return
      }

      let currentRowLines: InvoiceFormRowLine[] = []
      lines.map((line, lineIndex) => {
        if (isInvoiceFormRowLine(line)) {
          currentRowLines.push(line)
        }

        if (line.type === InvoiceLineType.Subtotal) {
          const {
            subtotalExcludingVat: currentSubtotalExcludingVat,
            subtotalIncludingVat: currentSubtotalIncludingVat,
          } = sumRowLineTotals(currentRowLines)

          setValue(`invoiceLines.${lineIndex}.subtotalIncludingVat`, currentSubtotalIncludingVat)
          setValue(`invoiceLines.${lineIndex}.subtotalExcludingVat`, currentSubtotalExcludingVat)

          if (hasLineStructureChanged) {
            line.subtotalIncludingVat = currentSubtotalIncludingVat
            line.subtotalExcludingVat = currentSubtotalExcludingVat
          }

          currentRowLines = []
        }

        return line
      })

      if (hasLineStructureChanged) {
        setValue('invoiceLines', lines)
      }
    },
    [getValues, setValue],
  )

  // Recomputes and stores a single row line's totals. Shared by every handler that changes an
  // input feeding into the line total (receiver/product/unit price/quantity/discount/discount type)
  const applyLineTotals = useCallback(
    (
      lineIndex: number,
      line: Pick<InvoiceFormRowLine, 'discount' | 'quantity' | 'unitPrice' | 'vatRatePercent'>,
      discountType: InvoiceDiscountType,
      currency: string,
    ) => {
      const { totalExcludingVat, totalIncludingVat } = calculateRowLineTotals({
        currency,
        discountType,
        isCreditNote,
        line,
      })
      setValue(`invoiceLines.${lineIndex}.totalExcludingVat`, totalExcludingVat)
      setValue(`invoiceLines.${lineIndex}.totalIncludingVat`, totalIncludingVat)
    },
    [isCreditNote, setValue],
  )

  const handleReceiverChange = useCallback(
    (receiver?: InvoiceFormReceiver) => {
      if (isEmpty(receiver) || shouldPreventVatUpdates) {
        return
      }

      const lines = getValues('invoiceLines')

      for (const [lineIndex, line] of lines.entries()) {
        if (!isInvoiceFormRowLine(line)) {
          continue
        }

        const lineVatRates = getVatRateList(line.productType, receiver)

        if (!lineVatRates.length) {
          setVatRate(lineIndex, undefined)
        }

        const currentVatRate = getVatRate({
          vatRateId: line.vatRateId,
          vatRates: lineVatRates,
        })

        if (currentVatRate?.id === line.vatRateId) {
          continue
        }

        const vatRate = getVatRate({
          // predefinedTag is used as a fallback if vatRateId is not on the list,
          // so we want to use the selected product's vat rate first, if it's available
          vatRateId: line?.productFromSearch?.vatRateId || line.vatRateId,
          vatRates: lineVatRates,
        })

        if (vatRate) {
          setVatRate(lineIndex, vatRate)
          const line = getValues('invoiceLines')[lineIndex]

          if (isInvoiceFormRowLine(line)) {
            applyLineTotals(
              lineIndex,
              { ...line, vatRatePercent: vatRate.rate },
              getValues('discountType'),
              getValues('currency'),
            )
          }
        }
      }
      recalculateSubtotals()
    },
    [applyLineTotals, getValues, getVatRate, getVatRateList, recalculateSubtotals, setVatRate, shouldPreventVatUpdates],
  )

  const handleProductChange = useCallback(
    (lineIndex: number, product: Partial<Product> | undefined) => {
      if (isEmpty(product)) {
        return
      }

      const vatRates = getVatRateList(product?.productType)

      // if the product does not contain a valid VAT rate, we'll get the default one here
      const productVatRate = getVatRate({ vatRateId: product?.vatRateId, vatRates })

      const line = getValues('invoiceLines')[lineIndex]

      if (!isInvoiceFormRowLine(line)) {
        return
      }

      setValue(`invoiceLines.${lineIndex}.productName`, product?.name)
      setValue(`invoiceLines.${lineIndex}.productType`, product?.productType)
      setValue(`invoiceLines.${lineIndex}.unitPrice`, product?.price)
      setValue(`invoiceLines.${lineIndex}.unit`, product?.unit ? product.unit : ProductUnit.Unit)

      if (!shouldPreventVatUpdates) {
        setVatRate(lineIndex, productVatRate)
      }

      const updatedLine = {
        ...line,
        unitPrice: product?.price ?? line.unitPrice,
        vatRatePercent: shouldPreventVatUpdates ? line.vatRatePercent : productVatRate?.rate,
      }

      // Totals are derived from unit price and resolved VAT rate (not product.total), including any line discount.
      applyLineTotals(lineIndex, updatedLine, getValues('discountType'), getValues('currency'))
      recalculateSubtotals()
    },
    [
      applyLineTotals,
      getVatRateList,
      getVatRate,
      getValues,
      setValue,
      shouldPreventVatUpdates,
      recalculateSubtotals,
      setVatRate,
    ],
  )

  const handleProductTypeChange = useCallback(
    (lineIndex: number, productType?: string) => {
      if (isNil(productType) || !isInvoiceProductType(productType) || shouldPreventVatUpdates) {
        return
      }

      const line = getValues('invoiceLines')[lineIndex]

      if (!isInvoiceFormRowLine(line)) {
        return
      }

      const vatRateId = line.vatRateId

      const vatRates = getVatRateList(productType)
      const vatRate = getVatRate({ vatRateId, vatRates })

      if (vatRate) {
        setVatRate(lineIndex, vatRate)
        applyLineTotals(
          lineIndex,
          { ...line, vatRatePercent: vatRate.rate },
          getValues('discountType'),
          getValues('currency'),
        )
        recalculateSubtotals()
      }
    },
    [applyLineTotals, getValues, getVatRate, getVatRateList, recalculateSubtotals, setVatRate, shouldPreventVatUpdates],
  )

  const handleUnitPriceChange = useCallback(
    (lineIndex: number, unitPrice?: number) => {
      if (isNil(unitPrice)) {
        return
      }

      const line = getValues('invoiceLines')[lineIndex]

      if (!isInvoiceFormRowLine(line)) {
        return
      }

      applyLineTotals(lineIndex, { ...line, unitPrice }, getValues('discountType'), getValues('currency'))
      recalculateSubtotals()
    },
    [applyLineTotals, getValues, recalculateSubtotals],
  )

  const handleQuantityChange = useCallback(
    (lineIndex: number, quantity?: number) => {
      if (isNil(quantity)) {
        return
      }

      const line = getValues('invoiceLines')[lineIndex]

      if (!isInvoiceFormRowLine(line)) {
        return
      }

      applyLineTotals(lineIndex, { ...line, quantity }, getValues('discountType'), getValues('currency'))
      recalculateSubtotals()
    },
    [applyLineTotals, getValues, recalculateSubtotals],
  )

  const handleTotalIncludingVatChange = useCallback(
    (lineIndex: number, totalIncludingVat?: number) => {
      if (isNil(totalIncludingVat)) {
        return
      }

      const line = getValues('invoiceLines')[lineIndex]

      if (!isInvoiceFormRowLine(line)) {
        return
      }

      const discountType = getValues('discountType')
      const currency = getValues('currency')

      // Don't back-solve unitPrice while the discount exceeds the line's base - the resulting
      // negative totalIncludingVat gets clamped to 0 by CurrencyInput and re-enters here as if
      // the user had typed 0, which would otherwise force unitPrice down to match the discount.
      const { validationIssues } = InvoiceDiscountCalculator.calculateLine(line, discountType, currency)
      if (InvoiceDiscountCalculator.hasDiscountExceedsBaseIssue(validationIssues)) {
        return
      }

      const totalExcludingVat = getAmountExcludingVat(totalIncludingVat, line.vatRatePercent)

      let subtotalExcludingVat: number

      if (discountType === InvoiceDiscountType.Percentage) {
        const remainingSubtotalRatio = 1 - line.discount / 100

        if (remainingSubtotalRatio <= 0) {
          return
        }

        subtotalExcludingVat = new NumberValue(totalExcludingVat).divide(remainingSubtotalRatio).getRounded()
      } else {
        subtotalExcludingVat = new NumberValue(totalExcludingVat).add(line.discount).getRounded()
      }

      const unitPrice = getUnitPrice(subtotalExcludingVat, line.quantity)

      setValue(`invoiceLines.${lineIndex}.unitPrice`, unitPrice)
      applyLineTotals(lineIndex, { ...line, unitPrice }, discountType, currency)
      recalculateSubtotals()
    },
    [applyLineTotals, getValues, recalculateSubtotals, setValue],
  )

  const handleVatRateChange = useCallback(
    (lineIndex: number, vatRateId?: string) => {
      if (isNil(vatRateId)) {
        return
      }

      const line = getValues('invoiceLines')[lineIndex]

      if (!isInvoiceFormRowLine(line)) {
        return
      }

      const vatRates = getVatRateList(line.productType)
      const vatRate = getVatRate({ vatRateId, vatRates })

      if (vatRate) {
        setVatRate(lineIndex, vatRate)
        applyLineTotals(
          lineIndex,
          { ...line, vatRatePercent: vatRate.rate },
          getValues('discountType'),
          getValues('currency'),
        )
        recalculateSubtotals()
      }
    },
    [applyLineTotals, getValues, getVatRateList, getVatRate, setVatRate, recalculateSubtotals],
  )

  const handleDiscountChange = useCallback(
    (lineIndex: number, discount?: number) => {
      if (isNil(discount)) {
        return
      }

      const line = getValues('invoiceLines')[lineIndex]

      if (!isInvoiceFormRowLine(line)) {
        return
      }

      const discountType = getValues('discountType')
      const discountValue = clampInvoiceDiscountValue(discount, discountType)
      setValue(`invoiceLines.${lineIndex}.discount`, discountValue)
      applyLineTotals(lineIndex, { ...line, discount: discountValue }, discountType, getValues('currency'))
      recalculateSubtotals()
    },
    [applyLineTotals, getValues, recalculateSubtotals, setValue],
  )

  const handleDiscountTypeChange = useCallback(
    (discountType: InvoiceDiscountType) => {
      const lines = getValues('invoiceLines')
      const currency = getValues('currency')

      for (const [lineIndex, line] of lines.entries()) {
        if (!isInvoiceFormRowLine(line)) {
          continue
        }

        const discount = clampInvoiceDiscountValue(line.discount, discountType)

        if (discount !== line.discount) {
          setValue(`invoiceLines.${lineIndex}.discount`, discount)
        }

        applyLineTotals(lineIndex, { ...line, discount }, discountType, currency)
      }
      recalculateSubtotals()
    },
    [applyLineTotals, getValues, recalculateSubtotals, setValue],
  )

  const handleClearLineDiscounts = useCallback(() => {
    const lines = getValues('invoiceLines')
    const currency = getValues('currency')

    for (const [lineIndex, line] of lines.entries()) {
      if (!isInvoiceFormRowLine(line)) {
        continue
      }

      setValue(`invoiceLines.${lineIndex}.discount`, 0, { shouldDirty: true })
      applyLineTotals(lineIndex, { ...line, discount: 0 }, getValues('discountType'), currency)
    }
    recalculateSubtotals()
  }, [applyLineTotals, getValues, recalculateSubtotals, setValue])

  return {
    handleDiscountChange,
    handleDiscountTypeChange,
    handleClearLineDiscounts,
    handleProductChange,
    handleProductTypeChange,
    handleQuantityChange,
    handleReceiverChange,
    handleTotalIncludingVatChange,
    handleUnitPriceChange,
    handleVatRateChange,
    recalculateSubtotals,
  }
}
